/**
 * GroupedMatrixView Component (Phase 9.3)
 *
 * Displays skill matrix aggregated by shift/position/zone.
 * Each group shows as a collapsible card with SOP scores.
 */

import { useState } from 'react';
import { ChevronDown, Clock, Briefcase, Locate, Users, AlertTriangle } from 'lucide-react';
import type { SkillMatrixGroupedResponse, SkillMatrixGroup } from '@guild/shared';
import { getScoreColor, getScoreBadgeColor } from '../../lib/score-colors';
import { Tooltip } from '../ui/Tooltip';

interface GroupedMatrixViewProps {
  data: SkillMatrixGroupedResponse;
  onCellClick?: (groupId: string, sopId: string) => void;
}


// Group icon based on type
function GroupIcon({ groupBy }: { groupBy: 'shift' | 'position' | 'station' }) {
  switch (groupBy) {
    case 'shift':
      return <Clock size={18} className="text-blue-600" />;
    case 'position':
      return <Briefcase size={18} className="text-purple-600" />;
    case 'station':
      return <Locate size={18} className="text-amber-600" />;
  }
}

// Group header color based on type
function getGroupHeaderColor(groupBy: 'shift' | 'position' | 'station') {
  switch (groupBy) {
    case 'shift':
      return 'border-l-blue-500';
    case 'position':
      return 'border-l-purple-500';
    case 'station':
      return 'border-l-amber-500';
  }
}

interface GroupCardProps {
  group: SkillMatrixGroup;
  groupBy: 'shift' | 'position' | 'station';
  isExpanded: boolean;
  onToggle: () => void;
  onCellClick?: (groupId: string, sopId: string) => void;
}

function GroupCard({ group, groupBy, isExpanded, onToggle, onCellClick }: GroupCardProps) {
  const hasData = group.operatorCount > 0;
  const hasAssessments = group.groupStats && group.groupStats.totalAssessments > 0;

  return (
    <div
      className={`rounded-xl border border-gray-200 bg-white overflow-hidden border-l-4 ${getGroupHeaderColor(groupBy)} shadow-sm`}
    >
      {/* Group Header */}
      <button
        onClick={onToggle}
        disabled={!hasData}
        className={`w-full flex items-center justify-between p-4 transition-colors ${
          hasData ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default opacity-60'
        }`}
      >
        <div className="flex items-center gap-3">
          <GroupIcon groupBy={groupBy} />
          <div className="text-left">
            <span className="font-semibold text-gray-900">{group.groupName}</span>
            {group.groupCode && (
              <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                {group.groupCode}
              </span>
            )}
            <div className="flex items-center gap-3 mt-0.5 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users size={14} />
                {group.operatorCount} {group.operatorCount === 1 ? 'operator' : 'operators'}
              </span>
              {hasAssessments && (
                <span>{group.groupStats?.totalAssessments} assessments</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Skill gap badge */}
          {group.groupStats && group.groupStats.skillGapCount > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 rounded bg-red-50 text-red-700 text-xs font-medium">
              <AlertTriangle size={12} />
              {group.groupStats.skillGapCount} skill {group.groupStats.skillGapCount === 1 ? 'gap' : 'gaps'}
            </span>
          )}

          {/* Group average score badge */}
          {group.groupStats?.overallAverage !== null && group.groupStats?.overallAverage !== undefined && (
            <span
              className={`px-3 py-1.5 rounded-lg text-sm font-bold border ${getScoreBadgeColor(
                group.groupStats.overallAverage
              )}`}
            >
              {group.groupStats.overallAverage.toFixed(0)} avg
            </span>
          )}

          {/* Empty state */}
          {(!hasAssessments && hasData) && (
            <span className="text-sm text-gray-400">No assessments yet</span>
          )}

          {/* Expand/collapse arrow */}
          {hasData && (
            <ChevronDown
              size={20}
              className={`text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
          )}
        </div>
      </button>

      {/* Expanded Content - SOP Breakdown */}
      {isExpanded && hasData && (
        <div className="border-t border-gray-100">
          {group.cells.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No assessment data for this group yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      SOP
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Avg Score
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Assessments
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Operators Assessed
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Skill Gaps
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.sops.map((sop, idx) => {
                    const cell = group.cells.find((c) => c.sopId === sop.id);
                    return (
                      <tr
                        key={sop.id}
                        className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}
                      >
                        <td className="px-4 py-3 max-w-[200px]">
                          <Tooltip content={sop.title} side="bottom">
                            <span className="text-sm font-medium text-gray-900 line-clamp-2 cursor-help">
                              {sop.title}
                            </span>
                          </Tooltip>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {cell?.averageScore !== null && cell?.averageScore !== undefined ? (
                            <button
                              onClick={() => onCellClick?.(group.groupId, sop.id)}
                              className={`inline-flex min-w-[48px] justify-center px-2.5 py-1 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-200 ${getScoreColor(
                                cell.averageScore, 'badge'
                              )} hover:ring-2 hover:ring-guild-green/30`}
                            >
                              {cell.averageScore.toFixed(0)}
                            </button>
                          ) : (
                            <span
                              className={`inline-flex min-w-[48px] justify-center px-2.5 py-1 rounded-lg text-sm font-semibold ${getScoreColor(
                                null, 'badge'
                              )}`}
                            >
                              —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          {cell?.assessmentCount ?? 0}
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">
                          <span className="text-gray-900">{cell?.operatorsAssessed ?? 0}</span>
                          <span className="text-gray-400"> / {group.operatorCount}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {cell && cell.skillGapCount > 0 ? (
                            <span className="inline-flex px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-medium">
                              {cell.skillGapCount}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function GroupedMatrixView({ data, onCellClick }: GroupedMatrixViewProps) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedGroups(new Set(data.groups.filter((g) => g.operatorCount > 0).map((g) => g.groupId)));
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  // Separate regular groups from unassigned
  const regularGroups = data.groups.filter((g) => g.groupId !== 'unassigned');
  const unassignedGroup = data.groups.find((g) => g.groupId === 'unassigned');

  const groupsWithData = data.groups.filter((g) => g.operatorCount > 0);

  if (data.groups.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-8 text-center shadow-sm">
        <div className="flex flex-col items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <GroupIcon groupBy={data.groupBy} />
          </div>
          <p className="text-sm font-medium text-gray-900">
            No {data.groupBy === 'shift' ? 'shifts' : data.groupBy === 'position' ? 'positions' : 'zones'} configured
          </p>
          <p className="text-sm text-gray-500">
            Add {data.groupBy === 'shift' ? 'shifts' : data.groupBy === 'position' ? 'positions' : 'zones'} in Settings to see grouped data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Expand/Collapse controls */}
      {groupsWithData.length > 1 && (
        <div className="flex justify-end gap-2">
          <button
            onClick={expandAll}
            className="text-sm text-gray-600 hover:text-guild-green transition-colors"
          >
            Expand all
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={collapseAll}
            className="text-sm text-gray-600 hover:text-guild-green transition-colors"
          >
            Collapse all
          </button>
        </div>
      )}

      {/* Regular groups */}
      {regularGroups.map((group) => (
        <GroupCard
          key={group.groupId}
          group={group}
          groupBy={data.groupBy}
          isExpanded={expandedGroups.has(group.groupId)}
          onToggle={() => toggleGroup(group.groupId)}
          onCellClick={onCellClick}
        />
      ))}

      {/* Unassigned group (if any) */}
      {unassignedGroup && unassignedGroup.operatorCount > 0 && (
        <>
          <div className="border-t border-gray-200 my-4" />
          <GroupCard
            group={unassignedGroup}
            groupBy={data.groupBy}
            isExpanded={expandedGroups.has('unassigned')}
            onToggle={() => toggleGroup('unassigned')}
            onCellClick={onCellClick}
          />
        </>
      )}
    </div>
  );
}

export default GroupedMatrixView;
